import React, { useRef, useMemo, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useStore from '../store';
import { projectTo3D } from '../utils/coordinates';
import { COLORS } from '../utils/colors';
import { filterNodes } from '../graphLoader';

function NodeMesh({ nodes }) {
  const { filters, setSelectedNode, setHoveredNode, hoveredNode, selectedNode, clickedNodes } = useStore();
  
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchQuery ||
      filters.categories.length > 0 ||
      filters.agencies.length > 0 ||
      filters.minValue > 0 ||
      filters.maxValue < 100000000
    );
  }, [filters]);
  
  const filteredNodes = useMemo(() => {
    if (!hasActiveFilters) return [];
    return filterNodes(nodes, filters);
  }, [nodes, filters, hasActiveFilters]);
  
  return (
    <group>
      {filteredNodes.map(node => (
        <Node
          key={node.id}
          node={node}
          isHovered={hoveredNode?.id === node.id}
          isSelected={selectedNode?.id === node.id}
          isClicked={clickedNodes.has(node.id)}
          onHover={setHoveredNode}
          onClick={setSelectedNode}
        />
      ))}
    </group>
  );
}

function Node({ node, isHovered, isSelected, isClicked, onHover, onClick }) {
  const meshRef = useRef();
  const outlineRef = useRef();
  const [hover, setHover] = useState(false);
  
  const position = useMemo(() => {
    return projectTo3D(node.lat, node.lon, 0.01);
  }, [node.lat, node.lon]);
  
  // SMALLER SIZE - Clean separation for 500+ nodes
  const size = 0.010;
  const outlineSize = size * 1.3; // 30% bigger for border
  
  const color = useMemo(() => {
    if (isSelected) return new THREE.Color('#FF4D4D'); // Red when selected
    if (isClicked) return new THREE.Color('#FFD700'); // Gold when previously clicked
    return new THREE.Color(node.color); // Original color (cyan/green/amber)
  }, [node.color, isSelected, isClicked]);
  
  const outlineColor = useMemo(() => {
    if (isSelected || hover) return new THREE.Color('#FFFFFF'); // White outline on hover/select
    return new THREE.Color('#05f3ff'); // Cyan outline normally
  }, [isSelected, hover]);
  
  useFrame(() => {
    if (!meshRef.current || !outlineRef.current) return;
    const scale = (hover || isSelected) ? 1.6 : 1;
    meshRef.current.scale.setScalar(scale);
    outlineRef.current.scale.setScalar(scale);
  });
  
  const handlePointerOver = (e) => {
    e.stopPropagation();
    setHover(true);
    onHover(node);
    document.body.style.cursor = 'pointer';
  };
  
  const handlePointerOut = () => {
    setHover(false);
    onHover(null);
    document.body.style.cursor = 'default';
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    onClick(node);
  };
  
  return (
    <group position={position}>
      {/* Outline/Border sphere - slightly bigger */}
      <mesh
        ref={outlineRef}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <sphereGeometry args={[outlineSize, 8, 8]} />
        <meshBasicMaterial 
          color={outlineColor} 
          transparent 
          opacity={hover || isSelected ? 0.8 : 0.4}
        />
      </mesh>
      
      {/* Main node sphere - solid color */}
      <mesh
        ref={meshRef}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <sphereGeometry args={[size, 8, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
    </group>
  );
}

export default NodeMesh;